import React, { useEffect, useMemo, useState } from 'react'
import { useNls } from '../../../nls/nlsProvider'
import { useAuth } from '../../../context/useAuth'
import { useMyAccessScope } from '../../../hooks/useMyAccessScope'
import type {
  OpsBookable,
  OpsBookingPlanPolicy,
  OpsBookingSession,
  OpsCustomer,
  OpsMembership,
  OpsPlace,
  OpsSector,
} from '../../../types/ops'
import { DEFAULT_OPS_BOOKING_PLAN_POLICY, OPS_SECTORS } from '../../../types/ops'
import { getEnabledBookingSectors } from '../../../utils/workspaceModules'
import type { Workspace } from '../../../types/workspace'
import { Button, StatusPill } from '../../Common'
import {
  bookSession,
  subscribeBookables,
  subscribeBookingPlan,
  subscribeBookingSessions,
  subscribeMembershipsByCustomer,
} from '../../../services/opsBookingsService'
import { subscribeToPlaces } from '../../../services/opsPlacesService'
import { expandOccurrences } from '../../../types/bookingsCore'
import { toLocalIsoDate } from '../../../utils/localDateTimeInput'
import { formatMemberBookingError } from './memberBookingsErrorMapper'

export interface MemberBookTabProps {
  workspace: Workspace
  customer: OpsCustomer
  isPhone?: boolean
}

function createOpId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `op_${Date.now()}`
}

export function MemberBookTab({
  workspace,
  customer,
  isPhone = false,
}: MemberBookTabProps): React.ReactElement {
  const { t } = useNls()
  const { currentUser } = useAuth()
  const currentUid = currentUser?.uid ?? ''
  const scope = useMyAccessScope(workspace.id)
  const isRestricted = Boolean(scope.restricted)

  const [bookablesBySector, setBookablesBySector] = useState<Record<string, OpsBookable[]>>({})
  const [sessions, setSessions] = useState<Record<string, OpsBookingSession>>({})
  const [places, setPlaces] = useState<Record<string, OpsPlace>>({})
  const [memberships, setMemberships] = useState<OpsMembership[]>([])
  const [planDocPolicy, setPlanDocPolicy] = useState<OpsBookingPlanPolicy | null>(null)
  const [busy, setBusy] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [successMsg, setSuccessMsg] = useState<string | null>(null)

  const [nowMs] = useState(() => Date.now())
  const todayIso = useMemo(() => toLocalIsoDate(nowMs), [nowMs])
  const activeSectors = useMemo(() => getEnabledBookingSectors(workspace), [workspace])
  const activeSectorsKey = activeSectors.join(',')

  // Subscriptions: memberships
  useEffect(() => {
    if (!workspace.id || !customer.id) return
    const unsub = subscribeMembershipsByCustomer(
      workspace.id,
      customer.id,
      (items) => setMemberships(items),
      (err) => setErrorMsg(formatMemberBookingError(err, t)),
    )
    return () => unsub()
  }, [workspace.id, customer.id, t])

  const activeMembership = memberships.find((m) => m.state === 'ACTIVE')
  const planPolicy: OpsBookingPlanPolicy =
    planDocPolicy ?? activeMembership?.planSnapshot?.policy ?? DEFAULT_OPS_BOOKING_PLAN_POLICY

  // Subscriptions: plan policy
  useEffect(() => {
    if (!activeMembership?.planId) {
      return
    }
    const unsub = subscribeBookingPlan(
      activeMembership.planId,
      (plan) => {
        setPlanDocPolicy(plan?.policy ?? null)
      },
      () => {
        setPlanDocPolicy(null)
      },
    )
    return () => {
      if (typeof unsub === 'function') unsub()
    }
  }, [activeMembership?.planId])

  // Subscriptions: bookables, places, and sessions for each enabled booking sector
  useEffect(() => {
    let unsubs: Array<() => void> = []
    if (!workspace.id || activeSectors.length === 0) return

    const maxWindowDays = planPolicy.bookAheadDays ?? DEFAULT_OPS_BOOKING_PLAN_POLICY.bookAheadDays
    const maxFutureIso = toLocalIsoDate(nowMs + maxWindowDays * 86400000)

    for (const sec of activeSectors) {
      const unsubBk = subscribeBookables(
        workspace.id,
        sec,
        (items) => {
          setBookablesBySector((prev) => ({ ...prev, [sec]: items }))
        },
        (err) => setErrorMsg(formatMemberBookingError(err, t)),
        { isRestricted, callerUid: currentUid, activeOnly: true },
      )
      if (typeof unsubBk === 'function') unsubs.push(unsubBk)

      const unsubPlaces = subscribeToPlaces(
        workspace.id,
        sec,
        (items) => {
          setPlaces((prev) => {
            const next = { ...prev }
            for (const p of items) {
              if (p.id) next[p.id] = p
            }
            return next
          })
        },
        (err) => setErrorMsg(formatMemberBookingError(err, t)),
        { isRestricted, callerUid: currentUid },
      )
      if (typeof unsubPlaces === 'function') unsubs.push(unsubPlaces)

      const unsubSess = subscribeBookingSessions(
        workspace.id,
        sec,
        todayIso,
        (items) => {
          setSessions((prev) => {
            const next = { ...prev }
            for (const s of items) {
              next[`${s.bookableId}_${s.date}`] = s
            }
            return next
          })
        },
        (err: Error) => setErrorMsg(formatMemberBookingError(err, t)),
        { isRestricted, callerUid: currentUid, toDate: maxFutureIso },
      )
      if (typeof unsubSess === 'function') unsubs.push(unsubSess)
    }

    return () => {
      unsubs.forEach((u) => {
        if (typeof u === 'function') u()
      })
      unsubs = []
    }
  }, [workspace.id, isRestricted, currentUid, todayIso, nowMs, activeSectors, activeSectorsKey, planPolicy.bookAheadDays, t])

  // Group bookables by sector and filter by membership plan
  const groupedBookables: Array<{ sector: OpsSector; items: OpsBookable[] }> = []
  let totalBookablesCount = 0

  for (const sec of activeSectors) {
    const list = bookablesBySector[sec] || []
    const filtered = list.filter((b) => {
      if (!b.active) return false
      if (activeMembership && activeMembership.planId) {
        if (b.planIds && b.planIds.length > 0 && !b.planIds.includes(activeMembership.planId)) {
          return false
        }
      }
      return true
    })
    if (filtered.length > 0) {
      groupedBookables.push({ sector: sec, items: filtered })
      totalBookablesCount += filtered.length
    }
  }

  const handleBookOccurrence = async (
    bookable: OpsBookable,
    date: string,
    sessionIsFull: boolean,
  ) => {
    if (busy) return
    setBusy(true)
    setErrorMsg(null)
    setSuccessMsg(null)
    try {
      const opId = createOpId()
      const res = await bookSession({
        workspaceId: workspace.id,
        bookableId: bookable.id,
        date,
        customerId: customer.id,
        membershipId: activeMembership?.id,
        waitlist: sessionIsFull,
        opId,
      })

      if (res.state === 'WAITLISTED') {
        setSuccessMsg(
          t('NLSMemberBookingsWaitlistSuccess', { position: res.waitlistPosition || 1 }),
        )
      } else {
        setSuccessMsg(t('NLSMemberBookingsBookingSuccess'))
      }
    } catch (err: unknown) {
      setErrorMsg(
        formatMemberBookingError(err, t, {
          action: 'book',
          blockedUntil: customer.blockedUntil,
        }),
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="memberBookTab">
      {errorMsg && (
        <div className="memberLinkBookingsCard__error" role="alert">
          {errorMsg}
        </div>
      )}

      {successMsg && (
        <div className="memberLinkBookingsCard__success" role="status">
          {successMsg}
        </div>
      )}

      {totalBookablesCount === 0 ? (
        <p className="memberBookingsListTab__empty">
          {t('NLSMemberBookingsEmptyBookables')}
        </p>
      ) : (
        <div className="memberBookTab__sectors">
          {groupedBookables.map((group) => (
            <div key={group.sector} className="memberBookTab__sectorSection">
              <div className="memberBookTab__sectorHeader">
                <StatusPill tone="neutral">{OPS_SECTORS[group.sector]?.key || group.sector}</StatusPill>
              </div>

              <div
                className={`memberBookTab__grid ${isPhone ? 'memberBookTab__grid--phone' : ''}`}
              >
                {group.items.map((bookable) => {
                  const effectiveWindow = Math.min(
                    bookable.bookAheadDays ?? planPolicy.bookAheadDays,
                    planPolicy.bookAheadDays,
                  )
                  const futureIso = toLocalIsoDate(Date.now() + effectiveWindow * 86400000)
                  const occurrences = bookable.pattern
                    ? expandOccurrences(bookable.pattern, [], todayIso, futureIso)
                    : []

                  return (
                    <div key={bookable.id} className="memberBookTab__card">
                      <div className="memberBookingsListTab__itemHeader">
                        <h3 className="memberBookingsListTab__itemTitle">{bookable.name}</h3>
                      </div>

                      <div className="memberBookTab__occurrences">
                        {occurrences.length === 0 ? (
                          <span className="memberBookingsListTab__empty">
                            {t('NLSMemberBookingsNoAlternateSessions')}
                          </span>
                        ) : (
                          occurrences.map((occDate) => {
                            const sessionKey = `${bookable.id}_${occDate}`
                            const session = sessions[sessionKey]
                            const capacity = session?.capacity ?? bookable.capacity
                            const booked = session?.booked ?? 0
                            const seatsLeft = Math.max(0, capacity - booked)
                            const sessionIsFull = seatsLeft <= 0
                            const placeName = bookable.placeId ? places[bookable.placeId]?.name : ''
                            const startTime = session?.startTime || bookable.pattern?.startTime || ''

                            return (
                              <div key={occDate} className="memberBookTab__occurrenceRow">
                                <div className="memberBookingsListTab__itemMeta">
                                  <strong>
                                    {occDate}
                                    {startTime ? ` · ${startTime}` : ''}
                                  </strong>
                                  <span>
                                    {placeName ? `${placeName} · ` : ''}
                                    {sessionIsFull
                                      ? t('NLSMemberBookingsSessionFull')
                                      : t('NLSMemberBookingsSeatsLeft', { count: seatsLeft })}
                                  </span>
                                </div>
                                <Button
                                  variant={sessionIsFull ? 'secondary' : 'primary'}
                                  size="sm"
                                  disabled={busy}
                                  onClick={() => handleBookOccurrence(bookable, occDate, sessionIsFull)}
                                >
                                  {sessionIsFull
                                    ? t('NLSMemberBookingsJoinWaitlistAction')
                                    : t('NLSMemberBookingsBookAction')}
                                </Button>
                              </div>
                            )
                          })
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
